/* Google Analytics 4 (GA4) client-side helper.
 *
 * Loading strategy:
 *   • gtag.js is injected in public/index.html so the loader runs BEFORE
 *     our React bundle. `gtag('config', ..., {send_page_view: false})`
 *     silences GA4's auto-pageview so our React Router listener owns it.
 *   • This module is the ONLY place we call `window.gtag(...)` — never
 *     import gtag directly in a component.
 *
 * PII contract:
 *   • Never pass mobile numbers, names, emails, WhatsApp numbers.
 *   • Only IDs (design_id, order_id), slugs (design_name, category,
 *     plot_size), amounts (integer INR), and UTM params.
 *   • Free-form fields (search_query) are truncated to 60 chars.
 *
 * UTM lifecycle:
 *   • Captured once on first app load from the URL query string.
 *   • Persisted in sessionStorage under `mn_utm` so they survive SPA
 *     navigation but reset when the tab closes (avoids stale attribution).
 *   • Read back and attached automatically to `homepage_viewed` +
 *     `purchase_completed` events.
 */

const GA_ID = "G-FB0M3146JT";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "ref"];
const UTM_STORAGE_KEY = "mn_utm";
const MAX_STR = 60;

function hasGtag() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

// -------------------- UTM capture / retrieval --------------------

/** Captures utm_* + ref from the URL, merges with any existing session
 *  storage entry (URL wins), and persists to sessionStorage. Called once
 *  from the AnalyticsProvider on app mount.
 */
export function captureUtmParams() {
  if (typeof window === "undefined") return {};
  try {
    const url = new URL(window.location.href);
    const fromUrl = {};
    UTM_KEYS.forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) fromUrl[k] = String(v).slice(0, MAX_STR);
    });
    if (Object.keys(fromUrl).length === 0) {
      // Nothing new in the URL — keep whatever was captured earlier.
      return getStoredUtmParams();
    }
    const merged = { ...getStoredUtmParams(), ...fromUrl };
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return {};
  }
}

/** Reads the persisted UTM bag. Returns `{}` when empty or unavailable. */
export function getStoredUtmParams() {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// -------------------- Event helpers --------------------

/** Fires a GA4 pageview. React Router doesn't trigger native pageviews
 *  on SPA navigation, so `usePageTracking` calls this on every location
 *  change. Safe to call before gtag.js has loaded — the call is dropped.
 */
export function trackPageView(path, title) {
  if (!hasGtag()) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: title || document.title || "",
      send_to: GA_ID,
    });
  } catch {
    // never throw from analytics
  }
}

/** Fires a custom event. `params` is sanitised — any key with a value
 *  longer than MAX_STR chars is truncated. Objects / arrays are
 *  stringified so a malformed input can't crash GA4's payload validator.
 */
export function trackEvent(name, params = {}) {
  if (!hasGtag()) return;
  try {
    const safe = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === null || v === undefined) return;
      if (typeof v === "number") {
        safe[k] = v;
      } else if (typeof v === "boolean") {
        safe[k] = v;
      } else {
        safe[k] = String(v).slice(0, MAX_STR);
      }
    });
    window.gtag("event", name, safe);
  } catch {
    // never throw from analytics
  }
}
